import { apiRequest } from "./baseApi";
import { parseJsonOrThrow } from "./responseUtils";

/**
 * Pontosense service layer.
 *
 * `apiRequest` returns the raw fetch Response (it does not parse JSON or
 * throw on non-2xx). Each helper parses via `responseUtils.parseJsonOrThrow`
 * so error messages stay aligned with GMA-1150 caregiver access APIs.
 */

export const fetchPontosenseValidationEvents = async (clientId) => {
  const res = await apiRequest(
    `clients/${clientId}/pontosense/validation-events`,
    { version: "v3" },
  );
  return parseJsonOrThrow(res, "Pontosense validation events");
};

export const bindPontosenseDevice = async (clientId, body) => {
  const res = await apiRequest(`clients/${clientId}/iot-devices`, {
    method: "POST",
    body,
    version: "v3",
  });
  return parseJsonOrThrow(res, "Pontosense bind device");
};

/** GMA-1557: list the client's bound Pontosense sensors. Returns `{ devices }`. */
export const fetchPontosenseDevices = async (clientId) => {
  const res = await apiRequest(`clients/${clientId}/iot-devices`, {
    version: "v3",
  });
  return parseJsonOrThrow(res, "Pontosense devices");
};

/**
 * GMA-1557: remove (soft-unbind) a Pontosense sensor from the client. The
 * backend sets the device row to status:inactive — it is never destroyed — so
 * stale events from a removed sensor stop routing to caregivers. Admin-only.
 */
export const unbindPontosenseDevice = async (clientId, sensorSn) => {
  const res = await apiRequest(
    `clients/${clientId}/iot-devices/${encodeURIComponent(sensorSn)}`,
    { method: "DELETE", version: "v3" },
  );
  return parseJsonOrThrow(res, "Pontosense remove device");
};

export const setPontosenseIntegrationEnabled = async (clientId, enabled) => {
  const res = await apiRequest(`clients/${clientId}/pontosense-integration`, {
    method: "PATCH",
    body: { enabled },
    version: "v3",
  });
  return parseJsonOrThrow(res, "Pontosense integration toggle");
};

/**
 * GMA-1865 — authoritative read of the per-client Pontosense toggle.
 * GET /api/v3/clients/:clientId/pontosense-integration
 * Prefer this over the client roster field so admin on/off cannot snap back
 * when the flag is missing/stale on GET /clients.
 */
export const fetchPontosenseIntegrationEnabled = async (clientId) => {
  const res = await apiRequest(`clients/${clientId}/pontosense-integration`, {
    version: "v3",
  });
  const data = await parseJsonOrThrow(res, "Pontosense integration status");
  // BE uses enabled-by-default (`!== false`).
  return data?.pontosenseIntegrationEnabled !== false;
};

export const fetchPontosenseCareWebLauncher = async (clientId) => {
  const res = await apiRequest(
    `pontosense/care-web-launcher?clientId=${clientId}`,
    { version: "v3" },
  );
  return parseJsonOrThrow(res, "Pontosense care web launcher");
};

// GMA-1296: staff bell feed (cross-client unread Pontosense alerts).
export const fetchPontosenseNotifications = async () => {
  const res = await apiRequest("notifications?source=pontosense", {
    version: "v3",
  });
  return parseJsonOrThrow(res, "Pontosense notifications");
};

/** GMA-1399: dismiss a Pontosense bell row (sets iot_events.acknowledged_at). */
export const acknowledgePontosenseEvent = async (eventId) => {
  const res = await apiRequest(`iot-events/${eventId}/acknowledge`, {
    method: "POST",
    version: "v1",
  });
  return parseJsonOrThrow(res, "Pontosense acknowledge");
};

/**
 * GMA-1478: clear the whole bell in one request — the backend acknowledges
 * every in-scope unread IoT event for the caller. Returns `{ cleared }`.
 */
export const dismissAllNotifications = async () => {
  const res = await apiRequest("notifications/clear-all", {
    method: "POST",
    version: "v3",
  });
  return parseJsonOrThrow(res, "Clear all notifications");
};
